import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  ENTER_UID,
  HOME,
  LEARN_DASHBOARD,
  LEARN_TOPIC_SELECTION,
  LESSON_VIEW,
  TOPIC_LESSON,
} from '../../routeHandling/RoutesNomenclature';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import { RootState } from '../../reduxStore/store';
import {
  deleteLocalStorage,
  deserify,
  Loader,
  NoContentCard,
  PAL_BASE_URL,
  QrCodeUrlTypeEnum,
  setLocalStorage,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { getSubjectsMap } from '../../utils/icons';
import { onResourceClick } from '../../utils/learn';
import { Code, ConnectError } from '@bufbuild/connect';
import { apiFetch } from '@geneo2-web/services-clients';
import { SessionModeEnum } from '@protos/learning_management/lms.db_pb';
import { setResourceRedirectionPath } from '../Learn/reducer/learn.slice';

const DEFAULT_QR_ERROR = 'Error Occurred. Invalid QR Code.';

const QR_NAVIGATION_RESOLVE_PATH =
  '/public/qr_code.service.v1.QRNavigationService/ResolveQRCodeNavigation';

function getErrorDisplayMessage(error: unknown, fallback: string): string {
  if (error instanceof ConnectError) {
    const raw = error.rawMessage?.trim();
    if (raw) {
      return raw;
    }
    const msg = error.message?.trim();
    if (msg) {
      return msg;
    }
    return fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

async function postResolveQrNavigation(qrCode: string): Promise<unknown> {
  const baseUrl = process.env.NX_GENEO_BACKEND_URL || '';
  const res = await apiFetch(
    `${PAL_BASE_URL && PAL_BASE_URL.startsWith('http') ? '' : baseUrl}${
      PAL_BASE_URL || ''
    }${QR_NAVIGATION_RESOLVE_PATH}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ qrCode }),
    }
  );
  if (!res.ok) {
    throw new Error('Failed to resolve QR code navigation');
  }
  return res.json();
}

export const ScreenResolver = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const searchParams = new URLSearchParams(location.search);
  const qrId = searchParams.get('QRId');
  const { userInfo } = deserify(
    useAppSelector((state: RootState) => state.auth)
  );
  const isLoggedIn = !!userInfo;

  const [loading, setLoading] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { UMSLoginAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();

  const getAndSetSubject = async (subjectId: number) => {
    try {
      const learnSubjects = userInfo?.learnSubjects || [];
      const subMap = getSubjectsMap(learnSubjects);
      const foundSubject = subMap[subjectId];
      if (foundSubject) {
        return foundSubject;
      }
      throw new Error('Subject mapping not found');
    } catch (err) {
      if (err instanceof Error) {
        throw err;
      }
      throw new Error('Subject mapping not found');
    }
  };

  const getDataFromQRId = async () => {
    try {
      if (!isLoggedIn) {
        navigate(ENTER_UID);
        return;
      }

      if (!qrId) {
        navigate(HOME);
        return;
      }

      setLoading('loading');
      setErrorMessage(null);

      const response =
        await UMSLoginAPIServiceV1ClientWithStatusCodeHandler.fetchStudentProfile(
          {
            studentProfileId: userInfo?.studentProfileId,
          }
        );

      const data = (await postResolveQrNavigation(qrId)) as {
        qrCodeNavigation?: {
          chapterId?: number;
          courseId?: number;
          subjectId?: number;
          topicId?: number;
          lessonPlanId?: string;
          resourceId?: string;
          urlType?: QrCodeUrlTypeEnum;
        };
      };

      const nav = data?.qrCodeNavigation;
      const chapterId = nav?.chapterId;
      const subjectId = nav?.courseId ?? nav?.subjectId;
      const topicId = nav?.topicId;
      const lessonPlanId = nav?.lessonPlanId;
      const resourceId = nav?.resourceId;

      if (isNaN(Number(subjectId))) {
        throw new Error(
          'Failed to resolve QR code navigation - Invalid subject'
        );
      }

      await getAndSetSubject(Number(subjectId));

      if (nav?.urlType === QrCodeUrlTypeEnum.SUBJECT) {
        navigate(`${LEARN_DASHBOARD}/${subjectId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.CHAPTER) {
        if (isNaN(Number(chapterId))) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter mapping'
          );
        }
        navigate(`${LEARN_TOPIC_SELECTION}/${subjectId}/${chapterId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.TOPIC) {
        if (isNaN(Number(chapterId)) && !isNaN(Number(topicId))) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter or topic'
          );
        }
        navigate(`${TOPIC_LESSON}/${subjectId}/${chapterId}/${topicId}`);
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.LESSON_PLAN) {
        if (
          isNaN(Number(chapterId)) &&
          !isNaN(Number(topicId)) &&
          !lessonPlanId
        ) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid chapter, topic, or Lesson Plan'
          );
        }
        if (!lessonPlanId) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid lesson plan'
          );
        }
        navigate(
          `${LESSON_VIEW}/${subjectId}/${chapterId}/${topicId}/${lessonPlanId}`
        );
      }

      if (nav?.urlType === QrCodeUrlTypeEnum.RESOURCE) {
        if (!resourceId) {
          throw new Error(
            'Failed to resolve QR code navigation - Invalid resource'
          );
        }
        dispatch(setResourceRedirectionPath(HOME));
        await onResourceClick(
          navigate,
          {
            resourceId,
            subjectId: subjectId ? Number(subjectId) : undefined,
            chapterId: chapterId ? Number(chapterId) : undefined,
            topicId: topicId ? Number(topicId) : undefined,
            lessonId: lessonPlanId,
          },
          lessonPlanId
            ? SessionModeEnum.SESSION_MODE_LESSON_RESOURCE
            : SessionModeEnum.SESSION_MODE_LEARN
        );
      }

      deleteLocalStorage('qrId');
      return;
    } catch (error) {
      if (
        error instanceof ConnectError &&
        error.code === Code.Unauthenticated
      ) {
        setLocalStorage('qrId', qrId);
      } else {
        deleteLocalStorage('qrId');
      }
      console.log(error);
      setErrorMessage(getErrorDisplayMessage(error, DEFAULT_QR_ERROR));
      setLoading('error');
    }
  };

  useEffect(() => {
    if (qrId) {
      console.log('setting qrId in local storage:', qrId);
      setLocalStorage('qrId', qrId);
    }
    getDataFromQRId();
  }, []);

  if (loading === 'loading') {
    return <Loader />;
  }

  if (loading === 'error') {
    return (
      <NoContentCard
        variant="error"
        icon="error"
        text={errorMessage ?? DEFAULT_QR_ERROR}
      />
    );
  }

  return <Box>Redirecting to the requested page...</Box>;
};
